import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { VendaItemRastreabilidade } from '../../../../../shared/services/models/vendas/venda-item-rastreabilidade.model';
import { VendaItemSerie } from '../../../../../shared/services/models/vendas/venda-item-serie.model';
import { ProdutoListStruct } from '../../../../../shared/services/structs/produto/produto-list.struct';
import { UtilService } from '../../../../../shared/services/util.service';
import { PrecoResolvido, resolverPrecoDoProduto } from './venda-item.preco';

/**
 * Monta o FormGroup de UM item do documento comercial (§6).
 *
 * Um grupo por bloco: cada aba do modal liga a sua própria fatia.
 *
 * Os blocos `dados`, `kit` e `comissao` são colunas de `venda_item` e viajam planos. Os tributos
 * também são colunas de `venda_item` e viajam planos (`codigoCstIcms` etc.), mas no form ficam em
 * grupos (`icms`, `ipi`, `pis`, `cofins`, `ibsCbs`, `difal`) com os nomes do item da NF-e de saída,
 * que é o que as abas de tributo usam. Lotes e séries (`lotes`, `series`) são valores simples, como no
 * `nfe-item.form.ts`: vão e voltam como vieram. A tradução fica num mapper só (`venda-item.mapa.ts`).
 */
export function montarFormItemVenda(fb: FormBuilder): FormGroup {
    return fb.group({
        dados: fb.group({
            idVendaItem: [null as number | null],
            sequencia: [0],

            idProduto: [null as number | null],
            descricao: [null as string | null],
            descricaoComplementar: [null as string | null],
            codigoSku: [null as string | null],
            gtinEan: [null as string | null],

            quantidade: [1 as number | null, Validators.min(0)],
            idUnidadeMedida: [null as number | null],
            siglaUnidade: [null as string | null],

            // Vem do cadastro da unidade do produto e decide a MASCARA da quantidade: unidade que
            // nao fraciona aceita inteiro, e mostrar casas decimais ali so convida a erro.
            isUnidadeMedidaFracionada: [true],

            // Só da tela (fora do MAPA do request), como a unidade fracionada: o controle de lote e de
            // série do cadastro do produto decide a aba Lote/série do modal e a seleção exigida.
            isControlaLotes: [false],
            isControlaSeries: [false],

            // Preço bruto (o da lista) e desconto só aparecem no modal, com "Exibir campo preço de
            // lista e desconto nos itens" ligado (§6.2); o controle existe sempre.
            //
            // `min(0)` nos numéricos é cinto e suspensório: a máscara já recusa o sinal na
            // digitação, mas o valor também chega por colagem, por preenchimento do produto e
            // pelo documento carregado do servidor — e nenhum desses passa pela máscara.
            precoBrutoUnitario: [null as number | null, Validators.min(0)],
            descontoPercentual: [0 as number | null, Validators.min(0)],
            precoLiquidoUnitario: [0 as number | null, Validators.min(0)],
            valorTotal: [0 as number | null, Validators.min(0)],
            // D21: a folha deriva/rateia e grava; na tela existem para a comissão e o teto baterem
            // com o servidor. `valorDescontoRateado` NÃO é exibido em lugar nenhum.
            descontoUnitario: [null as number | null],
            valorDescontoRateado: [null as number | null],
            // (líquido - bruto) / bruto x 100, com sinal. Quem calcula é a API; a tela só lê.
            diferencaPrecoBrutoLiquidoPercentual: [null as number | null],

            prazoDisponibilidadeDias: [null as number | null, Validators.min(0)],
            idListaPreco: [null as number | null],
            isListaPrecoManual: [false],

            idLocalizacaoEstoque: [null as number | null],

            pesoBruto: [null as number | null],
            pesoLiquido: [null as number | null],
            idTipoItemSped: [null as number | null],
            numeroPedidoCliente: [null as string | null],
            numeroItemPedido: [null as number | null],

            // A natureza é editável NO ITEM e sobrepõe a do documento apenas nesta linha (§6.4).
            idNatOp: [null as number | null],
            codigoCfop: [null as string | null],
            codigoNcm: [null as string | null],
            codigoBeneficioFiscal: [null as string | null],

            // Os grupos editados à mão ficam em `tributosInformadosManualmente` e o recálculo os preserva.
            tributosInformadosManualmente: [[] as string[]],
        }),

        // O kit não é uma linha do array: estes campos ficam nos COMPONENTES, e o agrupador da
        // tela é derivado de `numeroKit` (§6.9).
        kit: fb.group({
            idProdutoKit: [null as number | null],
            numeroKit: [null as number | null],
            quantidadeKit: [null as number | null],
            precoKit: [null as number | null],
            percentualDescontoKit: [null as number | null],

            // Só da TELA (o mapa servidor↔form não os conhece, então não vão no request): o que
            // o cabeçalho do grupo mostra e o que o recálculo do kit precisa lembrar de cada
            // componente. Na carga do documento vêm do enriquecimento e da derivação.
            descricaoKit: [null as string | null],
            codigoSkuKit: [null as string | null],
            isPrecoFixoKit: [false],
            quantidadeComposicao: [null as number | null],
            precoBaseComponente: [null as number | null],
        }),

        // Só de tela (fora do MAPA do request): a regra de múltiplo do produto (§6.2.3), para a
        // lista mostrar a equivalência e reabrir o diálogo. A folha não guarda; o orquestrador
        // devolve no GET a partir do cadastro do produto.
        multiplo: fb.group({
            isControlaMultiplo: [false],
            fatorMultiplo: [null as number | null],
            unidadeMultiplo: [null as string | null],
        }),
        comissao: fb.group({
            percentualComissao: [null as number | null],
            valorComissao: [null as number | null],
        }),

        // Grupos de tributo: os mesmos campos do item da NF-e de saída (`nfe-item.form.ts`). TODOS os
        // campos ficam no form: cada um é uma coluna de `venda_item`, e campo que faltasse aqui iria
        // nulo para o banco. O de-para com as colunas planas fica em `venda-item.mapa.ts`.
        icms: fb.group({
            codigoOrigemMercadoria: [null as string | null],
            codigoCst: [null as string | null],
            codigoCsosn: [null as string | null],
            codigoModalidadeBaseCalculo: [null as string | null],
            valorBaseCalculo: [null as number | null],
            percentualReducaoBaseCalculo: [null as number | null],
            percentualAliquota: [null as number | null],
            valorIcmsOperacao: [null as number | null],
            valorIcms: [null as number | null],
            valorIcmsDesonerado: [null as number | null],
            codigoMotivoDesoneracao: [null as string | null],
            codigoIndicadorDeducaoDesoneracao: [null as string | null],
            valorBaseFcp: [null as number | null],
            percentualFcp: [null as number | null],
            valorFcp: [null as number | null],
            codigoModalidadeBaseCalculoSt: [null as string | null],
            percentualMvaSt: [null as number | null],
            percentualReducaoBaseCalculoSt: [null as number | null],
            valorBaseCalculoSt: [null as number | null],
            percentualAliquotaSt: [null as number | null],
            valorIcmsSt: [null as number | null],
            valorBaseFcpSt: [null as number | null],
            percentualFcpSt: [null as number | null],
            valorFcpSt: [null as number | null],
            valorBaseCalculoStRetido: [null as number | null],
            percentualSuporteStRetido: [null as number | null],
            valorIcmsStRetido: [null as number | null],
            valorBaseFcpStRetido: [null as number | null],
            percentualFcpStRetido: [null as number | null],
            valorFcpStRetido: [null as number | null],
            percentualDiferimento: [null as number | null],
            valorIcmsDiferido: [null as number | null],
            valorBaseCalculoEfetivo: [null as number | null],
            percentualAliquotaEfetiva: [null as number | null],
            valorIcmsEfetivo: [null as number | null],
            percentualCredito: [null as number | null],
            valorCredito: [null as number | null],
            codigoCreditoPresumidoIcms: [null as string | null],
            percentualCreditoPresumidoIcms: [null as number | null],
            valorCreditoPresumidoIcms: [null as number | null],
            quantidadeBaseMonofasica: [null as number | null],
            valorAliquotaAdremMonofasica: [null as number | null],
            valorIcmsMonofasico: [null as number | null],
            quantidadeBaseMonofasicaRetencao: [null as number | null],
            valorAliquotaAdremMonofasicaRetencao: [null as number | null],
            valorIcmsMonofasicoRetencao: [null as number | null],
            quantidadeBaseMonofasicaRetido: [null as number | null],
            valorAliquotaAdremMonofasicaRetido: [null as number | null],
            valorIcmsMonofasicoRetido: [null as number | null],
            percentualReducaoAdrem: [null as number | null],
        }),
        ipi: fb.group({
            codigoCst: [null as string | null],
            codigoEnquadramento: [null as string | null],
            cnpjProdutor: [null as string | null],
            codigoSelo: [null as string | null],
            quantidadeSelo: [null as number | null],
            valorBaseCalculo: [null as number | null],
            percentualAliquota: [null as number | null],
            quantidadeUnidade: [null as number | null],
            valorUnidade: [null as number | null],
            valorIpi: [null as number | null],
        }),
        pis: fb.group({
            codigoCst: [null as string | null],
            valorBaseCalculo: [null as number | null],
            percentualAliquota: [null as number | null],
            quantidadeBaseCalculo: [null as number | null],
            valorAliquotaUnidade: [null as number | null],
            valorPis: [null as number | null],
            valorBaseCalculoSt: [null as number | null],
            percentualAliquotaSt: [null as number | null],
            quantidadeBaseCalculoSt: [null as number | null],
            valorAliquotaUnidadeSt: [null as number | null],
            valorPisSt: [null as number | null],
            codigoIndicadorComposicaoSt: [null as string | null],
        }),
        cofins: fb.group({
            codigoCst: [null as string | null],
            valorBaseCalculo: [null as number | null],
            percentualAliquota: [null as number | null],
            quantidadeBaseCalculo: [null as number | null],
            valorAliquotaUnidade: [null as number | null],
            valorCofins: [null as number | null],
            valorBaseCalculoSt: [null as number | null],
            percentualAliquotaSt: [null as number | null],
            quantidadeBaseCalculoSt: [null as number | null],
            valorAliquotaUnidadeSt: [null as number | null],
            valorCofinsSt: [null as number | null],
            codigoIndicadorComposicaoSt: [null as string | null],
        }),
        ibsCbs: fb.group({
            codigoCst: [null as string | null],
            codigoClassificacaoTributaria: [null as string | null],
            indicadorDoacao: [null as string | null],
            valorBaseCalculo: [null as number | null],
            percentualAliquotaIbsUf: [null as number | null],
            valorIbsUf: [null as number | null],
            percentualAliquotaIbsMunicipal: [null as number | null],
            valorIbsMunicipal: [null as number | null],
            percentualAliquotaCbs: [null as number | null],
            valorCbs: [null as number | null],
            valorIbs: [null as number | null],
            percentualDiferimentoIbsUf: [null as number | null],
            valorDiferimentoIbsUf: [null as number | null],
            percentualDiferimentoIbsMunicipal: [null as number | null],
            valorDiferimentoIbsMunicipal: [null as number | null],
            percentualDiferimentoCbs: [null as number | null],
            valorDiferimentoCbs: [null as number | null],
            valorDevolucaoIbsUf: [null as number | null],
            valorDevolucaoIbsMunicipal: [null as number | null],
            valorDevolucaoCbs: [null as number | null],
            percentualReducaoIbsUf: [null as number | null],
            percentualAliquotaEfetivaIbsUf: [null as number | null],
            percentualReducaoIbsMunicipal: [null as number | null],
            percentualAliquotaEfetivaIbsMunicipal: [null as number | null],
            percentualReducaoCbs: [null as number | null],
            percentualAliquotaEfetivaCbs: [null as number | null],
            codigoTipoAlcZfmCbs: [null as string | null],
            numeroProcessoSuframa: [null as string | null],
            percentualAliquotaEfetivaRegularCbsAlcZfm: [null as number | null],
            valorTributoRegularCbsAlcZfm: [null as number | null],
            percentualAliquotaEfetivaRegularIbsUf: [null as number | null],
            valorRegularIbsUf: [null as number | null],
            percentualAliquotaEfetivaRegularIbsMunicipal: [null as number | null],
            valorRegularIbsMunicipal: [null as number | null],
            percentualAliquotaEfetivaRegularCbs: [null as number | null],
            valorRegularCbs: [null as number | null],
            percentualAliquotaCompraGovernoIbsUf: [null as number | null],
            valorCompraGovernoIbsUf: [null as number | null],
            percentualAliquotaCompraGovernoIbsMunicipal: [null as number | null],
            valorCompraGovernoIbsMunicipal: [null as number | null],
            percentualAliquotaCompraGovernoCbs: [null as number | null],
            valorCompraGovernoCbs: [null as number | null],
            quantidadeBaseMonofasicaPadrao: [null as number | null],
            valorAliquotaIbsUnidadePadrao: [null as number | null],
            valorIbsMonofasicoPadrao: [null as number | null],
            valorAliquotaCbsUnidadePadrao: [null as number | null],
            valorCbsMonofasicoPadrao: [null as number | null],
            quantidadeBaseMonofasicaRetencao: [null as number | null],
            valorAliquotaIbsUnidadeRetencao: [null as number | null],
            valorIbsMonofasicoRetencao: [null as number | null],
            valorAliquotaCbsUnidadeRetencao: [null as number | null],
            valorCbsMonofasicoRetencao: [null as number | null],
            quantidadeBaseMonofasicaRetido: [null as number | null],
            valorAliquotaIbsUnidadeRetido: [null as number | null],
            valorIbsMonofasicoRetido: [null as number | null],
            valorAliquotaCbsUnidadeRetido: [null as number | null],
            valorCbsMonofasicoRetido: [null as number | null],
            percentualDiferimentoIbsMonofasico: [null as number | null],
            valorIbsMonofasicoDiferido: [null as number | null],
            percentualDiferimentoCbsMonofasico: [null as number | null],
            valorCbsMonofasicoDiferido: [null as number | null],
            valorTransferenciaCreditoIbs: [null as number | null],
            valorTransferenciaCreditoCbs: [null as number | null],
            anoMesAjusteCompetencia: [null as string | null],
            valorAjusteIbs: [null as number | null],
            valorAjusteCbs: [null as number | null],
            valorEstornoCreditoIbs: [null as number | null],
            valorEstornoCreditoCbs: [null as number | null],
            valorBaseCreditoPresumido: [null as number | null],
            codigoCreditoPresumido: [null as string | null],
            percentualCreditoPresumidoIbs: [null as number | null],
            valorCreditoPresumidoIbs: [null as number | null],
            valorCreditoPresumidoSuspensivoIbs: [null as number | null],
            percentualCreditoPresumidoCbs: [null as number | null],
            valorCreditoPresumidoCbs: [null as number | null],
            valorCreditoPresumidoSuspensivoCbs: [null as number | null],
            anoMesCreditoPresumidoZfm: [null as string | null],
            codigoTipoCreditoPresumidoZfm: [null as string | null],
            valorCreditoPresumidoZfm: [null as number | null],
        }),
        difal: fb.group({
            valorBaseCalculo: [null as number | null],
            percentualFcpDestino: [null as number | null],
            percentualAliquotaInternaDestino: [null as number | null],
            percentualAliquotaInterestadual: [null as number | null],
            percentualPartilha: [null as number | null],
            valorFcpDestino: [null as number | null],
            valorIcmsDestino: [null as number | null],
            valorIcmsRemetente: [null as number | null],
        }),

        // Lote e série selecionados do estoque (valores simples: vão e voltam como vieram, como no item da
        // NF-e de saída). Produto que controla lote fecha a quantidade com a soma de `quantidadeLote`; o que
        // controla série tem uma por unidade; a conferência final é do orquestrador no salvamento.
        lotes: [[] as VendaItemRastreabilidade[]],
        series: [[] as VendaItemSerie[]],
    });
}

/**
 * Casas do PERCENTUAL de desconto. Preco e quantidade seguem a configuracao da empresa
 * (Configuracoes >> Gerais >> Casas decimais, §6.2); o percentual nao tem configuracao propria.
 */
const CASAS_PERCENTUAL = 2;

/** Quando a configuracao da empresa nao chega, o mesmo fallback do `MaskService`. */
const CASAS_PRECO_PADRAO = 2;

/**
 * O % de desconto entre dois preços. O KIT usa: o desconto do kit vira o desconto de cada
 * componente, e o preço do kit informado abaixo da lista vira desconto do kit. A comissão por
 * faixa também (`percentualDaComissao`): a faixa lê o desconto efetivo do preço contra o bruto.
 *
 * No item avulso o preço NÃO preenche o desconto: a diferença para a lista fica em
 * `diferencaPrecoBrutoLiquidoPercentual`, que a API calcula.
 */
export function percentualDoPreco(precoBrutoUnitario: number | null, precoLiquidoUnitario: number | null): number {
    const bruto = Number(precoBrutoUnitario ?? 0);
    const liquido = Number(precoLiquidoUnitario ?? 0);

    if (bruto <= 0) return 0;

    // Preço acima da lista é ZERO, não negativo: acréscimo não é desconto.
    if (liquido >= bruto) return 0;

    return UtilService.arredondarAbnt((1 - liquido / bruto) * 100, CASAS_PERCENTUAL);
}

/**
 * Liga o desconto (%) ao preço líquido do item, num sentido só: digitou o %, o preço nasce dele
 * nas casas de preço (`precoDoPercentual`). Alterar o preço NÃO mexe no desconto: a diferença
 * para a lista a API grava, e o teto confere o desconto efetivo (`descontoEfetivoDoItem`), que já
 * inclui a redução de preço.
 *
 * Devolve a assinatura para quem liga cancelar ao morrer (o modal liga na cópia que edita).
 */
export function ligarPrecoEDesconto(dados: FormGroup, casasPreco: () => number): Subscription | undefined {
    return dados.get('descontoPercentual')?.valueChanges.subscribe((percentual) => {
        // Sem preço bruto não há de que descontar: calcular zeraria o preço negociado do produto
        // que não está em lista nenhuma.
        if (Number(dados.get('precoBrutoUnitario')?.value ?? 0) <= 0) return;

        dados.get('precoLiquidoUnitario')?.setValue(
            precoDoPercentual(dados.get('precoBrutoUnitario')?.value, percentual, casasPreco()),
            { emitEvent: false },
        );
    });
}

/**
 * O caminho inverso: digitou o desconto, o preco unitario acompanha.
 *
 * Arredonda nas casas de PRECO da empresa, nao nas duas do percentual: o resultado e o preco
 * unitario do produto, e a §6.2 manda a configuracao de preco reger preco de lista e preco
 * unitario. Com a empresa em 3 casas, fixar 2 aqui transformava 680,044 com 10% em R$ 612,04
 * onde o correto e R$ 612,040 — e era esse preco truncado que ia para `venda_item.preco_liquido_unitario`,
 * coluna `numeric(30,10)`, e para a base dos tributos.
 *
 * O TOTAL da linha continua em 2 casas (`venda-item.calculo.ts`): quantidade x preco ja e
 * dinheiro a cobrar, e centavo e a menor parte que existe.
 */
export function precoDoPercentual(
    precoBrutoUnitario: number | null,
    percentual: number | null,
    casasPreco: number = CASAS_PRECO_PADRAO,
): number {
    const bruto = Number(precoBrutoUnitario ?? 0);
    const desconto = Number(percentual ?? 0);

    if (bruto <= 0) return 0;

    return UtilService.arredondarAbnt(bruto * (1 - desconto / 100), casasPreco);
}

/** O que a resolução de preço (§6.2.2.3) precisa saber do DOCUMENTO para decidir. */
/** O "% Desconto Sugerido" da lista, saneado (0 a 100, 2 casas); 0 quando a lista não tem. */
export function descontoSugeridoDa(lista: { percentualDescontoSugerido?: number | null } | null | undefined): number {
    const pct = Number(lista?.percentualDescontoSugerido ?? 0) || 0;

    return UtilService.arredondarAbnt(Math.min(100, Math.max(0, pct)), CASAS_PERCENTUAL);
}

export interface ContextoDePreco {
    /** A lista escolhida no cabeçalho. Nula quando o documento não tem uma. */
    idListaCabecalho: number | null;

    /** Configurações ▸ Vendas ▸ Gerais ▸ "Priorizar menor preço entre listas". */
    priorizarMenorPreco: boolean;

    /**
     * Casas decimais de PREÇO da empresa (Configurações ▸ Gerais ▸ Casas decimais).
     *
     * Viaja no contexto, e não por `@Input` novo, porque quem reaplica lista já recebe este
     * objeto inteiro — e o preço recalculado tem de sair nas mesmas casas do campo que o exibe.
     */
    casasDecimaisPreco: number;

    /** Casas decimais de QUANTIDADE da empresa — a quantidade do componente de kit sai nelas (folha idem). */
    casasDecimaisQuantidade: number;
}

/**
 * Preenche a linha com o que vem do CADASTRO do produto.
 *
 * Fica aqui, e não no componente da linha, porque o produto entra por dois caminhos — o campo
 * Item e a busca avançada — e um item lançado por um deles não pode nascer diferente do outro.
 *
 * O `isUnidadeMedidaFracionada` (da unidade de medida do produto) precisa chegar junto: é ele que troca a máscara da quantidade
 * entre decimal e inteiro, e uma segunda consulta deixaria o campo aceitando casas que a unidade
 * não tem.
 *
 * Devolve a lista que definiu o preço para quem chamou registrar as candidatas e desenhar o
 * rastreio — a linha sozinha não tem como saber de onde o número veio.
 */
export function preencherDoProduto(
    linha: FormGroup,
    produto: ProdutoListStruct,
    contexto: ContextoDePreco,
): PrecoResolvido {
    const resolvido = resolverPrecoDoProduto(
        produto.listasPreco,
        contexto.idListaCabecalho,
        contexto.priorizarMenorPreco,
    );

    // Sem lista vigente o item entra com preço ZERO e fica pendente (§6.2.2.3) — e não com o
    // `produto.preco`, que é o da lista padrão e entraria por baixo do pano, sem origem nenhuma
    // para mostrar no rastreio. O leque de candidatas já inclui a padrão quando ela alcança.
    const preco = resolvido.lista?.preco ?? 0;
    // "% Desconto Sugerido" da lista resolvida pré-preenche o desconto unitário (Pedro, 24/09). É só
    // um valor inicial: o vendedor muda como quiser, e o teto do perfil continua valendo.
    const sugerido = descontoSugeridoDa(resolvido.lista);

    linha.get('dados')?.patchValue({
        idProduto: produto.idProduto,
        descricao: produto.descricao,
        codigoSku: produto.codigoSku ?? null,
        gtinEan: produto.gtinEan ?? null,
        idUnidadeMedida: produto.idUnidadeMedida ?? null,
        siglaUnidade: produto.unidade ?? null,
        isUnidadeMedidaFracionada: produto.isUnidadeMedidaFracionada !== false,
        isControlaLotes: produto.isControlaLotes === true,
        isControlaSeries: produto.isControlaSeries === true,
        codigoNcm: produto.ncm ?? null,
        pesoBruto: produto.pesoBrutoKg ?? null,
        pesoLiquido: produto.pesoLiquidoKg ?? null,
        precoBrutoUnitario: preco,
        precoLiquidoUnitario: precoDoPercentual(preco, sugerido, contexto.casasDecimaisPreco),
        descontoPercentual: sugerido,
        idListaPreco: resolvido.lista?.idListaPreco ?? null,
        // Produto recém-escolhido nunca é escolha manual: quem escolhe lista à mão é o popover.
        isListaPrecoManual: false,
    });

    // A seleção de lote e série é do produto anterior: trocar o produto a descarta.
    linha.patchValue({ lotes: [], series: [] });

    linha.get('multiplo')?.patchValue({
        isControlaMultiplo: produto.isControlaMultiplo === true,
        fatorMultiplo: produto.fatorMultiplo ?? null,
        unidadeMultiplo: produto.unidadeMultiplo ?? null,
    }, { emitEvent: false });

    return resolvido;
}

/**
 * Reaplica uma lista ESCOLHIDA À MÃO no item (§6.2.5), ou devolve o item à regra automática.
 *
 * `idListaPreco` nulo é o "Seguir a regra automática" do popover: a resolução roda de novo e o
 * item volta a acompanhar o cabeçalho. Com lista escolhida, `isListaPrecoManual` fica ligado — é
 * ele que protege a linha da próxima troca de lista no cabeçalho.
 *
 * O DESCONTO é preservado, e não o preço: o vendedor negociou "10% de desconto", não "R$ 47,30".
 * Trocar a lista muda de quanto era; quanto se tirou continua sendo decisão dele.
 */
export function aplicarListaNoItem(
    linha: FormGroup,
    lista: { idListaPreco: number; preco: number } | null,
    contexto: ContextoDePreco,
    candidatas: ProdutoListStruct['listasPreco'],
): PrecoResolvido {
    const resolvido = lista
        ? { lista: (candidatas ?? []).find((l) => l.idListaPreco === lista.idListaPreco) ?? null, origem: 'manual' as const }
        : resolverPrecoDoProduto(candidatas, contexto.idListaCabecalho, contexto.priorizarMenorPreco);

    const dados = linha.get('dados');
    const precoBrutoUnitario = resolvido.lista?.preco ?? 0;
    // Lista nova com desconto sugerido substitui o % do item; sem sugestão, o % negociado fica.
    const sugerido = descontoSugeridoDa(resolvido.lista);
    const descontoPercentual = sugerido > 0 ? sugerido : Number(dados?.get('descontoPercentual')?.value ?? 0);

    dados?.patchValue({
        precoBrutoUnitario,
        descontoPercentual,
        precoLiquidoUnitario: precoDoPercentual(precoBrutoUnitario, descontoPercentual, contexto.casasDecimaisPreco),
        idListaPreco: resolvido.lista?.idListaPreco ?? null,
        isListaPrecoManual: resolvido.origem === 'manual',
    });

    return resolvido;
}
